import { createFileRoute, Link } from "@tanstack/react-router";
import {
  MonitorPlay,
  RefreshCw,
  Palette,
  Search,
  Megaphone,
  Sparkles,
  Video,
  ShieldCheck,
  Gauge,
  Home,
  ArrowLeft,
  Facebook,
  Instagram,
} from "lucide-react";
import { FOOTER_SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import { HeroNav } from "@/components/ui/hero/HeroNav";
import { Footer } from "@/components/ui/footer";
import { lazy, Suspense } from "react";
import logo from "@/assets/mind-media-logo.svg";

const ContactSection = lazy(() =>
  import("@/components/sections/ContactSection").then((m) => ({ default: m.ContactSection })),
);

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title: "Services — Mind Media" },
      {
        name: "description",
        content:
          "Explore all Mind Media services: web design, branding, SEO, paid ads, AI solutions, care plans, and more.",
      },
      { property: "og:title", content: "Services — Mind Media" },
      {
        property: "og:description",
        content:
          "Explore all Mind Media services: web design, branding, SEO, paid ads, AI solutions, care plans, and more.",
      },
    ],
  }),
  component: ServicesPage,
});

const SERVICES = [
  { href: "/websites", icon: MonitorPlay, title: "Web Design", subtitle: "(one-time projects)" },
  {
    href: "/design-subscription",
    icon: RefreshCw,
    title: "Design Subscription",
    subtitle: "(monthly)",
  },
  { href: "/branding", icon: Palette, title: "Branding", subtitle: "(identity & strategy)" },
  { href: "/seo", icon: Search, title: "SEO", subtitle: "(get found on Google)" },
  { href: "/paid-ads", icon: Megaphone, title: "Paid Ads", subtitle: "(traffic on demand)" },
  {
    href: "/ai-services",
    icon: Sparkles,
    title: "AI Solutions",
    subtitle: "(automate the busywork)",
  },
  {
    href: "/1-on-1-website-help",
    icon: Video,
    title: "1-on-1 Website Help",
    subtitle: "(live screen-share sessions)",
  },
  { href: "/care-plans", icon: ShieldCheck, title: "Care Plans", subtitle: "(we keep it running)" },
  {
    href: "/site-speed",
    icon: Gauge,
    title: "Speed & Performance",
    subtitle: "(make it load fast)",
  },
] as const;

function ServicesPage() {
  return (
    <main className="min-h-screen bg-background">
      <div className="relative isolate overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-40 -bottom-40 z-0 h-[420px] w-[420px] rounded-full bg-hero-glow-warm opacity-35 blur-3xl sm:-left-56 sm:-bottom-56 sm:h-[720px] sm:w-[720px] sm:blur-[120px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-40 -top-40 z-0 h-[420px] w-[420px] rounded-full bg-hero-glow-cool opacity-40 blur-3xl sm:-right-56 sm:-top-56 sm:h-[720px] sm:w-[720px] sm:blur-[120px]"
        />
        <section className="relative z-20 pb-8 text-center sm:pb-10">
          <HeroNav
            logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
            navigation={[
              { label: "Work", href: "/#work" },
              { label: "About", href: "/#about" },
              { label: "Services", href: "/services" },
              { label: "Pricing", href: "/#pricing" },
              { label: "FAQ", href: "/#faq" },
              { label: "Contact", href: "#contact" },
            ]}
            ctaButton={{ label: "Book a Meeting", href: "#contact" }}
          />

          <div className="relative mx-auto max-w-7xl px-4 pt-10 sm:px-6 sm:pt-16">
            <div className="mx-auto max-w-3xl text-center">
              <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
                All Services
              </h1>
              <p className="mt-2 text-lg text-muted-foreground">
                Build, grow, or maintain your site.
              </p>
            </div>
          </div>
        </section>

        <section className="relative z-10 mx-auto max-w-7xl px-4 pb-8 pt-4 sm:px-6 sm:pb-10 sm:pt-6">
          <div className="rounded-3xl border border-border/60 bg-card p-4 shadow-sm sm:p-6">
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5">
              {SERVICES.map(({ href, icon: Icon, title, subtitle }) => (
                <Link
                  key={href}
                  to={href}
                  className="group flex flex-col items-center justify-center gap-3 rounded-2xl border border-border/60 bg-card px-4 py-8 text-center transition-all hover:-translate-y-0.5 hover:border-border hover:shadow-md sm:py-10"
                >
                  <span
                    className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full"
                    style={{ backgroundColor: "#f9f3fe" }}
                  >
                    <Icon className="h-8 w-8" strokeWidth={2.25} style={{ color: "#3972ee" }} />
                  </span>
                  <span className="mt-2 flex flex-col items-center leading-tight">
                    <span className="text-lg font-bold text-foreground">{title}</span>
                    <span className="mt-1 text-sm text-muted-foreground">{subtitle}</span>
                  </span>
                </Link>
              ))}

              <Link
                to="/"
                className="group flex flex-col items-center justify-center gap-3 rounded-2xl border border-border/60 bg-card px-4 py-8 text-center transition-all hover:-translate-y-0.5 hover:border-border hover:shadow-md sm:py-10"
              >
                <span
                  className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: "#f9f3fe" }}
                >
                  <Home className="h-8 w-8" strokeWidth={2.25} style={{ color: "#3972ee" }} />
                </span>
                <span className="mt-2 inline-flex items-center gap-2 text-lg font-bold text-foreground">
                  <ArrowLeft className="h-5 w-5" strokeWidth={2.5} />
                  Go to home
                </span>
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Suspense fallback={<div className="min-h-[400px]" />}>
        <ContactSection compact />
      </Suspense>

      <Footer
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        brandName="Mind Media"
        socialLinks={FOOTER_SOCIAL_LINKS}
        mainLinks={[
          { href: "/the-problem", label: "The Problem" },
          { href: "/#work", label: "Work" },
          { href: "/#pricing", label: "Pricing" },
          { href: "/#faq", label: "FAQ" },
          { href: "/blog", label: "Blog" },
          { href: "/services", label: "Services" },
        ]}
        legalLinks={[
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ]}
        copyright={{
          text: `© ${new Date().getFullYear()} Mind Media`,
          license: "All rights reserved",
        }}
      />
    </main>
  );
}
